/**
 * Unified Crypto Interface
 * Automatic fallback: Argon2id → PBKDF2
 *
 * Detection Strategy:
 * 1. Try Argon2id (argon2-browser library)
 * 2. If fails (WebAssembly unavailable), fallback to PBKDF2
 * 3. Detect envelope algorithm and route to correct implementation
 *
 * Usage:
 * - import * as crypto from "./utils/crypto"
 * - crypto.encryptPrivateKey(pem, password) → Auto-detects best method
 * - crypto.decryptPrivateKey(encrypted, envelope, password) → Auto-routes based on envelope
 */

import * as argon2Module from "./argon2.js";
import * as pbkdf2Module from "./pbkdf2.js";
import * as rsaModule from "./rsa.js";
import * as aesModule from "./aes.js";

// ========================================
// Auto-Detection State
// ========================================

let kdfImplementation = null; // Will be set to argon2Module or pbkdf2Module
let detectionAttempted = false;

/**
 * Detects which KDF implementation is available
 * Tries Argon2id first, falls back to PBKDF2
 *
 * @returns {Promise<"argon2id"|"pbkdf2">}
 */
async function detectKdfImplementation() {
  if (detectionAttempted && kdfImplementation !== null) {
    return kdfImplementation === argon2Module ? "argon2id" : "pbkdf2";
  }

  detectionAttempted = true;

  try {
    // Test Argon2id with a simple password derivation
    console.log("[Crypto] Detecting KDF implementation...");
    const testPassword = "test";
    const testSalt = new Uint8Array(16);

    await argon2Module.deriveKeyFromPassword(testPassword, testSalt);

    kdfImplementation = argon2Module;
    console.log("[Crypto] ✅ Argon2id available (primary method)");
    return "argon2id";
  } catch (error) {
    console.warn(
      "[Crypto] ⚠️ Argon2id unavailable, falling back to PBKDF2:",
      error.message,
    );
    kdfImplementation = pbkdf2Module;
    return "pbkdf2";
  }
}

// ========================================
// RSA Key Management (Re-exported)
// ========================================

/**
 * Generates RSA-2048 keypair for user
 * @returns {Promise<{publicKey: string, privateKey: string}>}
 */
export async function generateRSAKeypair() {
  return rsaModule.generateRSAKeypair();
}

/**
 * Encrypts MasterKey with recipient's public key
 * @param {Uint8Array} masterKeyBytes - MasterKey (32 bytes)
 * @param {string} recipientPublicKeyPem - Recipient's public key (PEM)
 * @returns {Promise<string>} - Encrypted MasterKey (Base64)
 */
export async function encryptMasterKeyWithPublicKey(
  masterKeyBytes,
  recipientPublicKeyPem,
) {
  return rsaModule.encryptMasterKeyWithPublicKey(
    masterKeyBytes,
    recipientPublicKeyPem,
  );
}

/**
 * Decrypts MasterKey with user's private key
 * @param {string} encryptedMasterKeyBase64 - Encrypted MasterKey (Base64)
 * @param {string} privateKeyPem - User's private key (PEM)
 * @returns {Promise<Uint8Array>} - Decrypted MasterKey (32 bytes)
 */
export async function decryptMasterKeyWithPrivateKey(
  encryptedMasterKeyBase64,
  privateKeyPem,
) {
  return rsaModule.decryptMasterKeyWithPrivateKey(
    encryptedMasterKeyBase64,
    privateKeyPem,
  );
}

// ========================================
// AES-GCM Encryption (Re-exported)
// ========================================

/**
 * Generates random MasterKey
 * @returns {Uint8Array} - 32-byte key
 */
export function generateMasterKey() {
  return aesModule.generateMasterKey();
}

/**
 * Encrypts recipient password entry
 * Uses single shared IV for both email and password (backend compatibility)
 *
 * @param {string} recipientEmail - Email address
 * @param {string} password - Password
 * @param {Uint8Array} masterKeyBytes - MasterKey (32 bytes)
 * @returns {Promise<{encryptedEmail, encryptedPassword, iv}>}
 */
export async function encryptRecipientPassword(
  recipientEmail,
  password,
  masterKeyBytes,
) {
  return aesModule.encryptRecipientPassword(
    recipientEmail,
    password,
    masterKeyBytes,
  );
}

/**
 * Decrypts recipient password entry
 * Uses single shared IV for both email and password (backend compatibility)
 *
 * @param {string} encryptedEmail - Encrypted email (Base64)
 * @param {string} encryptedPassword - Encrypted password (Base64)
 * @param {string} iv - Shared IV for both fields (Base64)
 * @param {Uint8Array} masterKeyBytes - MasterKey (32 bytes)
 * @returns {Promise<{email, password}>}
 */
export async function decryptRecipientPassword(
  encryptedEmail,
  encryptedPassword,
  iv,
  masterKeyBytes,
) {
  return aesModule.decryptRecipientPassword(
    encryptedEmail,
    encryptedPassword,
    iv,
    masterKeyBytes,
  );
}

// ========================================
// Private Key Encryption (Auto-Fallback)
// ========================================

/**
 * Encrypts user's private key with password
 * Auto-detects best KDF method (Argon2id or PBKDF2)
 *
 * @param {string} privateKeyPem - RSA private key (PEM)
 * @param {string} password - User password (Benutzer-Schlüssel)
 * @returns {Promise<{encryptedPrivateKey: string, envelope: object}>}
 */
export async function encryptPrivateKeyWithPassword(privateKeyPem, password) {
  const kdfType = await detectKdfImplementation();

  console.log(`[Crypto] Encrypting private key with ${kdfType.toUpperCase()}`);

  return kdfImplementation.encryptPrivateKeyWithPassword(
    privateKeyPem,
    password,
  );
}

/**
 * Decrypts user's private key with password
 * Auto-routes to correct KDF based on envelope.kdf.algorithm
 *
 * @param {string} encryptedPrivateKeyBase64 - Encrypted private key (Base64)
 * @param {object} envelope - PrivateKeyEnvelope metadata
 * @param {string} password - User password (Benutzer-Schlüssel)
 * @returns {Promise<string>} - Decrypted RSA private key (PEM)
 */
export async function decryptPrivateKeyWithPassword(
  encryptedPrivateKeyBase64,
  envelope,
  password,
) {
  // Route to correct implementation based on envelope
  const kdfAlgorithm = envelope.kdf.algorithm;

  console.log(`[Crypto] Decrypting private key with ${kdfAlgorithm}`);

  if (kdfAlgorithm === "Argon2id") {
    return argon2Module.decryptPrivateKeyWithPassword(
      encryptedPrivateKeyBase64,
      envelope,
      password,
    );
  } else if (kdfAlgorithm === "PBKDF2-HMAC-SHA256") {
    return pbkdf2Module.decryptPrivateKeyWithPassword(
      encryptedPrivateKeyBase64,
      envelope,
      password,
    );
  } else {
    throw new Error(`Unsupported KDF algorithm: ${kdfAlgorithm}`);
  }
}

// ========================================
// MasterKeyEnvelope Creation
// ========================================

/**
 * Creates MasterKeyEnvelope for PasswortDatenbankBenutzer
 *
 * @param {number} publicKeyVersion - Version of recipient's public key
 * @param {number} masterKeyVersion - Version of MasterKey (incremented on re-keying)
 * @returns {object} - MasterKeyEnvelope metadata
 */
export function createMasterKeyEnvelope(
  publicKeyVersion = 1,
  masterKeyVersion = 1,
) {
  return {
    algorithm: "RSA-OAEP-SHA256",
    publicKeyVersion,
    masterKeyVersion,
  };
}

// ========================================
// Utility Functions
// ========================================

/**
 * Gets current KDF implementation type
 * @returns {Promise<"argon2id"|"pbkdf2">}
 */
export async function getKdfImplementation() {
  return detectKdfImplementation();
}

/**
 * Validates PrivateKeyEnvelope structure
 * @param {object} envelope - PrivateKeyEnvelope
 * @returns {boolean}
 */
export function isValidPrivateKeyEnvelope(envelope) {
  if (!envelope || typeof envelope !== "object") return false;

  // Check required fields
  if (!envelope.kdf || !envelope.encryption || !envelope.version) return false;

  // Check KDF fields
  const kdf = envelope.kdf;
  if (!kdf.algorithm || !kdf.salt) return false;

  // Check encryption fields
  const enc = envelope.encryption;
  if (!enc.algorithm || !enc.nonce) return false;

  return true;
}

/**
 * Validates MasterKeyEnvelope structure
 * @param {object} envelope - MasterKeyEnvelope
 * @returns {boolean}
 */
export function isValidMasterKeyEnvelope(envelope) {
  if (!envelope || typeof envelope !== "object") return false;

  return (
    typeof envelope.algorithm === "string" &&
    typeof envelope.publicKeyVersion === "number" &&
    typeof envelope.masterKeyVersion === "number"
  );
}
